import axios from 'axios'
import { useMutation } from '@tanstack/react-query'

import type {
  TNewUploadAnalysedFile,
  TNewUploadAnalysedFileInput,
  TNewUploadFile,
} from './useNewUpload'
import { PrivateAPI } from '../utils/api'

type TCreateFileResponse = {
  file: {
    id: string
  }
  url: string
}

type TAnalyseUploadResponse = {
  analysedFile: TNewUploadAnalysedFile
}

type TAnalyseUploadMutationInput = {
  analysedFileId?: string
  file: TNewUploadFile
  input: TNewUploadAnalysedFileInput
}

type TAnalyseUploadMutationResult = {
  analysedFile: TNewUploadAnalysedFile
  fileId: string
}

const getAnalyseUploadErrorMessage = (err: unknown) => {
  const defaultMessage = 'An unexpected error occured'

  if (!axios.isAxiosError(err)) {
    return `${defaultMessage} [990]`
  }

  const status = err.response?.status

  if (!status) {
    return `${defaultMessage} [990]`
  }

  if (status >= 400 && status < 500) {
    return `${defaultMessage} [${status}]`
  }

  return defaultMessage
}

const useAnalyseUpload = () => {
  return useMutation<
    TAnalyseUploadMutationResult,
    Error,
    TAnalyseUploadMutationInput
  >({
    mutationFn: async ({ analysedFileId, file, input }) => {
      try {
        let uploadedFileId = file.id

        if (file.fileBlob) {
          const createFileResponse = await PrivateAPI<TCreateFileResponse>({
            url: '/files',
            method: 'post',
            data: {
              name: file.name,
              size: file.size,
              bucket: 'UPLOADS',
            },
          })

          await axios({
            url: createFileResponse.data.url,
            method: 'put',
            data: file.fileBlob,
            headers: file.fileBlob.type
              ? { 'Content-Type': file.fileBlob.type }
              : undefined,
          })

          uploadedFileId = createFileResponse.data.file.id
        }

        const analyseResponse = analysedFileId
          ? await PrivateAPI<TAnalyseUploadResponse>({
              url: `/uploads/analyse/${analysedFileId}`,
              method: 'patch',
              data: {
                fileId: uploadedFileId || undefined,
                input,
              },
            })
          : await PrivateAPI<TAnalyseUploadResponse>({
              url: '/uploads/analyse',
              method: 'post',
              data: {
                fileId: uploadedFileId,
                input,
              },
            })

        return {
          analysedFile: analyseResponse.data.analysedFile,
          fileId: analyseResponse.data.analysedFile.file.id,
        }
      } catch (err) {
        throw new Error(getAnalyseUploadErrorMessage(err))
      }
    },
  })
}

export default useAnalyseUpload
